import { Preferences } from '@capacitor/preferences'
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

import { LOGIN } from '@/modules/auth/routes'
import { useAuthStore } from '@/modules/auth/store'
import {
  ACTIVITY_THROTTLE_DELAY,
  BACKGROUND_INACTIVITY_TIMEOUT,
  FOREGROUND_INACTIVITY_TIMEOUT,
  MAX_SESSION_TIME,
  SESSION_CHECK_INTERVAL,
  STORAGE_KEYS,
} from '@/settings'

export function useActivityTracker() {
  let lastActivityTime: number | null = null
  let sessionCheckInterval: ReturnType<typeof setInterval> | null = null
  const authStore = useAuthStore()
  const router = useRouter()

  // Update last activity timestamp
  const updateActivity = () => {
    lastActivityTime = Date.now()
  }

  // Reset last activity time (used when returning from background)
  const resetActivityTime = () => {
    lastActivityTime = Date.now()
  }

  // Throttle function to avoid too many updates
  const throttle = (func: (...args: any[]) => void, delay: number) => {
    let lastCall = 0
    return (...args: any[]) => {
      const now = Date.now()
      if (now - lastCall >= delay) {
        lastCall = now
        func(...args)
      }
    }
  }

  const throttledUpdateActivity = throttle(updateActivity, ACTIVITY_THROTTLE_DELAY)

  // Check maximum session time
  async function checkMaxSessionTimeout(): Promise<boolean> {
    const { value: sessionStart } = await Preferences.get({ key: STORAGE_KEYS.SESSION_START })
    if (!sessionStart) {
      return false
    }

    const now = Date.now()
    const totalSessionTime = now - +sessionStart

    if (totalSessionTime > MAX_SESSION_TIME) {
      console.log('[Security] Max session time exceeded - logging out')

      // Stop checking
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval)
        sessionCheckInterval = null
      }

      // Keep device token for inactivity logout
      await authStore.logout(true)
      router.push({
        name: LOGIN,
        query: { reason: 'session_expired' },
      })
      return true
    }

    return false
  }

  // Check if user is inactive in foreground
  async function checkForegroundTimeout(): Promise<boolean> {
    // Skip check if lastActivityTime is not yet initialized
    if (lastActivityTime === null) {
      return false
    }

    const now = Date.now()
    const timeSinceLastActivity = now - lastActivityTime

    if (timeSinceLastActivity > FOREGROUND_INACTIVITY_TIMEOUT) {
      console.log('[Security] Foreground inactivity timeout - logging out')

      // Stop checking
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval)
        sessionCheckInterval = null
      }

      // Keep device token for inactivity logout
      await authStore.logout(true)
      router.push({
        name: LOGIN,
        query: { reason: 'inactivity_foreground' },
      })
      return true
    }

    return false
  }

  // Check background inactivity timeout
  async function checkBackgroundTimeout(timeSinceLastActive: number): Promise<boolean> {
    if (timeSinceLastActive > BACKGROUND_INACTIVITY_TIMEOUT) {
      console.log('[Security] ❌ Background inactivity timeout exceeded - logging out')
      await authStore.logout(true)
      router.push({
        name: LOGIN,
        query: { redirect_to: router.currentRoute.value.fullPath, reason: 'background_inactivity' },
      })
      return true
    }
    return false
  }

  // Check both max session timeout and foreground timeout
  const checkSessionTimeouts = async () => {
    // First check max session time
    const maxSessionExceeded = await checkMaxSessionTimeout()
    if (maxSessionExceeded) {
      return
    }

    // If user was in background, skip foreground timeout check
    // Background timeout check should take priority (handled in appStateChange)
    // lastActive is set when the app goes to background and removed after background check
    const { value: lastActive } = await Preferences.get({ key: STORAGE_KEYS.LAST_ACTIVE })
    if (lastActive) {
      // User was in the background - don't check foreground timeout yet
      // Background timeout will be checked in appStateChange first
      return
    }

    // Check foreground timeout only if the user wasn't in the background
    await checkForegroundTimeout()
  }

  // Setup activity listeners
  const setupListeners = () => {
    // Clear existing interval if any
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval)
      sessionCheckInterval = null
    }

    // Initialize lastActivityTime when starting to track
    lastActivityTime = Date.now()

    // Touch events (mobile)
    document.addEventListener('touchstart', throttledUpdateActivity as EventListener, {
      passive: true,
    })
    document.addEventListener('touchmove', throttledUpdateActivity as EventListener, {
      passive: true,
    })

    // Mouse events (desktop/tablet)
    document.addEventListener('mousedown', throttledUpdateActivity as EventListener)
    document.addEventListener('mousemove', throttledUpdateActivity as EventListener)
    document.addEventListener('wheel', throttledUpdateActivity as EventListener, { passive: true })

    // Keyboard events
    document.addEventListener('keydown', throttledUpdateActivity as EventListener)

    // Scroll events
    document.addEventListener('scroll', throttledUpdateActivity as EventListener, { passive: true })

    // Check session timeouts periodically (max session and foreground inactivity)
    sessionCheckInterval = setInterval(checkSessionTimeouts, SESSION_CHECK_INTERVAL)
  }

  // Remove activity listeners
  const removeListeners = () => {
    document.removeEventListener('touchstart', throttledUpdateActivity as EventListener)
    document.removeEventListener('touchmove', throttledUpdateActivity as EventListener)
    document.removeEventListener('mousedown', throttledUpdateActivity as EventListener)
    document.removeEventListener('mousemove', throttledUpdateActivity as EventListener)
    document.removeEventListener('wheel', throttledUpdateActivity as EventListener)
    document.removeEventListener('keydown', throttledUpdateActivity as EventListener)
    document.removeEventListener('scroll', throttledUpdateActivity as EventListener)

    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval)
      sessionCheckInterval = null
    }

    // Reset lastActivityTime when removing listeners
    lastActivityTime = null
  }

  onMounted(() => {
    if (authStore.user) {
      setupListeners()
    }
  })

  onUnmounted(() => {
    removeListeners()
  })

  return {
    setupListeners,
    removeListeners,
    updateActivity,
    resetActivityTime,
    checkMaxSessionTimeout,
    checkBackgroundTimeout,
  }
}
